/**
 * GeoDestinationCard — "This lives on <Portal> →" widget.
 *
 * Given an entity descriptor (country / state / city or a slug) and an
 * optional topic, this component hits `/api/route/resolve` and renders a
 * single, clear hand-off card pointing at the network portal that owns
 * that scope. It is the frontend surface of the Geographic Routing
 * Engine (SP-GEO-005) and is the primary tool SocialPulse.com uses to
 * avoid duplicating specialist portal content.
 *
 * Usage:
 *   <GeoDestinationCard country="India" state="Kerala" topic="news" />
 *   <GeoDestinationCard slug="united-states/california/san-jose" />
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, ExternalLink, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

function scopeLine({ country, state, city, topic }) {
  const parts = [];
  if (topic) parts.push(topic);
  const geo = [city, state, country].filter(Boolean).join(", ");
  if (geo) parts.push(geo);
  return parts.join(" · ");
}

export default function GeoDestinationCard({ country, state, city, slug, topic, testid = "geo-destination-card", compact = false }) {
  const [state_, setState_] = useState({ loading: true, data: null, err: null });

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (country) params.country = country;
    if (state) params.state = state;
    if (city) params.city = city;
    if (slug) params.slug = slug;
    if (topic) params.topic = topic;

    if (Object.keys(params).length === 0) {
      setState_({ loading: false, data: null, err: null });
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/route/resolve`, { params, timeout: 5000 });
        if (!cancelled) setState_({ loading: false, data: r.data, err: null });
      } catch (e) {
        if (!cancelled) setState_({ loading: false, data: null, err: e.message || "route failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [country, state, city, slug, topic]);

  if (state_.loading) {
    return (
      <div className={`inline-flex items-center gap-2 text-xs text-slate-500 ${compact ? "" : "py-3"}`} data-testid={`${testid}-loading`}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Resolving portal…
      </div>
    );
  }
  if (state_.err || !state_.data) return null;

  const { destination, url, stayed_on_socialpulse } = state_.data;
  const brand = destination?.brand_color || "#7C3AED";
  const label = scopeLine({ country, state, city, topic });

  if (compact) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener"
        data-testid={`${testid}-compact-${destination?.slug || "unknown"}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:text-slate-900 transition-all"
      >
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: brand }} />
        {destination?.name || "SocialPulse"}
        <ArrowUpRight className="w-3 h-3" />
      </a>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      data-testid={`${testid}-${destination?.slug || "unknown"}`}
      className="group block rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-md transition-all overflow-hidden"
    >
      <div className="h-1" style={{ background: brand }} />
      <div className="p-4 flex items-center gap-4">
        <span
          className="w-11 h-11 grid place-items-center rounded-xl text-xl ring-1 ring-white shadow-sm shrink-0"
          style={{ background: `${brand}18`, color: brand }}
        >
          {destination?.logo_emoji || "◐"}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[.16em] text-slate-500">
            {stayed_on_socialpulse ? "Hosted here on" : "Continue on"}
          </p>
          <h3 className="font-serif font-bold text-slate-900 text-base leading-tight truncate">
            {destination?.name}
          </h3>
          {label && (
            <p className="mt-0.5 text-xs text-slate-500 truncate">{label}</p>
          )}
        </div>
        <span className="hidden sm:inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold text-white shrink-0" style={{ background: brand }}>
          Open <ExternalLink className="w-3.5 h-3.5" />
        </span>
        <ArrowUpRight className="sm:hidden w-5 h-5 text-slate-400 shrink-0" />
      </div>
    </a>
  );
}
